import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import api from "../../services/api";
import BreadCrumbDiv from "../../components/common/BreadCrumbDiv";
import ButtonWrapDiv from "../../components/common/ButtonWrapDiv";
import ListWrapDiv from "../../components/common/ListWrapDiv";
import PageBar from "../../components/common/PageBar";
import BoxAreaDiv from "../../components/common/BoxAreaDiv";
import SearchWrapDiv from "../../components/common/SearchWrapDiv";
import SearchInput from "../../components/common/SearchInput";
import SearchButton from "../../components/common/SearchButton";
import CodeSelectBox from "../../components/common/CodeSelectBox";
import CustomDropDownTreeSelect from "../../components/common/CustomDropDownTreeSelect";
import { useMenuAuthInfo } from "../../services/GlobalUtil";
import { useTranslation } from "react-i18next";

const UserRequestManagementPage = () => {
  const pageSize = 10;
  const menuAuthInfo = useMenuAuthInfo();
  const { t } = useTranslation();

  const [approvalCurPage, setApprovalCurPage] = useState(1);
  const [unlockCurPage, setUnlockCurPage] = useState(1);

  const [approvalTotalCount, setApprovalTotalCount] = useState(0);
  const [unlockTotalCount, setUnlockTotalCount] = useState(0);

  const [isApprovalVisible, setIsApprovalVisible] = useState(false);
  const [approvalClass, setApprovalClass] = useState("unfold");
  const [isUnlockVisible, setIsUnlockVisible] = useState(false);
  const [unlockClass, setUnlockClass] = useState("unfold");

  const [id, setId] = useState("");
  const [locationDlr, setLocationDlr] = useState([]);
  const [status, setStatus] = useState("");
  const searchId = useRef("");
  const searchRegion = useRef([]);
  const searchStatus = useRef("");

  const [sbLocationDlr, setSbLocationDlr] = useState([]);
  const sbStatus = [
    { code: "", codeDesc: t("L0673") },
    { code: "Y", codeDesc: "Active" },
    { code: "N", codeDesc: "Disabled" },
  ];

  const [approvalData, setApprovalData] = useState([]);
  const [unlockData, setUnlockData] = useState([]);

  useEffect(() => {
    if (menuAuthInfo.view) {
      loadApprovalContentData();
    }
  }, [approvalCurPage]);

  useEffect(() => {
    if (menuAuthInfo.view) {
      loadUnlockContentData();
    }
  }, [unlockCurPage]);

  useEffect(() => {
    setUnlockClass(isUnlockVisible ? "unfold" : "fold");
  }, [isUnlockVisible]);

  useEffect(() => {
    setApprovalClass(isApprovalVisible ? "unfold" : "fold");
  }, [isApprovalVisible]);

  const loadLocationLocData = () => {
    if (sbLocationDlr != null && sbLocationDlr.length > 0) {
      return;
    }

    api
      .post("/common/location-loc-tree", {})
      .then((response) => {
        setSbLocationDlr(response.data);
      })
      .catch(() => {});
  };

  const loadApprovalContentData = () => {
    api
      .post("/manage/userrequest/joinview", {
        userId: searchId.current,
        sLocationLocs: searchRegion.current,
        sReason: searchStatus.current,
        page: approvalCurPage,
        size: pageSize,
      })
      .then((response) => {
        setApprovalTotalCount(response.data.totalCount);
        setApprovalData(response.data.content);
      })
      .catch(() => {});
  };

  const loadUnlockContentData = () => {
    api
      .post("/manage/userrequest/unlockview", {
        userId: searchId.current,
        sLocationLocs: searchRegion.current,
        sReason: searchStatus.current,
        page: unlockCurPage,
        size: pageSize,
      })
      .then((response) => {
        setUnlockTotalCount(response.data.totalCount);
        setUnlockData(response.data.content);
      })
      .catch(() => {});
  };

  const approvalPageChange = (data) => {
    if (approvalCurPage === data) {
      return;
    }
    setApprovalCurPage(data);
  };

  const unlockPageChange = (data) => {
    if (unlockCurPage === data) {
      return;
    }
    setUnlockCurPage(data);
  };

  const handleIdChange = (e) => {
    setId(e.target.value.trim());
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const handleSearch = () => {
    searchId.current = id;
    searchRegion.current = locationDlr;
    searchStatus.current = status;

    if (approvalCurPage === 1) {
      loadApprovalContentData();
    } else {
      setApprovalCurPage(1);
    }

    if (unlockCurPage === 1) {
      loadUnlockContentData();
    } else {
      setUnlockCurPage(1);
    }
  };

  const handleUnlockClick = () => {
    setIsUnlockVisible(!isUnlockVisible);
  };

  const handleApprovalClick = () => {
    setIsApprovalVisible(!isApprovalVisible);
  };

  const handleApprovalApprove = (data) => {
    api
      .post("/manage/userrequest/approvejoin", {
        userInfoId: data,
      })
      .then(() => {
        loadApprovalContentData();
      })
      .catch(() => {});
  };

  const handleApprovalReject = (data) => {
    api
      .post("/manage/userrequest/rejectjoin", {
        userInfoId: data,
      })
      .then(() => {
        loadApprovalContentData();
      })
      .catch(() => {});
  };

  const handleUnlockApprove = (data) => {
    api
      .post("/manage/userrequest/approveunlock", {
        userInfoId: data,
      })
      .then(() => {
        loadUnlockContentData();
      })
      .catch(() => {});
  };

  const handleUnlockReject = (data) => {
    api
      .post("/manage/userrequest/rejectunlock", {
        userInfoId: data,
      })
      .then(() => {
        loadUnlockContentData();
      })
      .catch(() => {});
  };

  return (
    <div>
      <BreadCrumbDiv items={[t("MENU:L0056"), t("MENU:L0071")]}></BreadCrumbDiv>

      <SearchWrapDiv>
        <ButtonWrapDiv>
          <SearchInput
            value={id}
            onChange={handleIdChange}
            placeholder={t("L0002")}
            maxLength={250}
          ></SearchInput>
          <CustomDropDownTreeSelect
            data={sbLocationDlr}
            placeholder={t("L0049")}
            isViewCode={true}
            flattenIntermediate={true}
            onChange={setLocationDlr}
            onFocus={loadLocationLocData}
          ></CustomDropDownTreeSelect>
          <div className="ml3"></div>
          <CodeSelectBox
            value={status}
            codes={sbStatus}
            onChange={handleStatusChange}
            isCodeVisible={false}
            multiple={false}
          ></CodeSelectBox>
          <SearchButton onClick={handleSearch}></SearchButton>
        </ButtonWrapDiv>
      </SearchWrapDiv>
      <BoxAreaDiv
        title={t("L0033") + ` (${approvalTotalCount})`}
        className={approvalClass}
        onClick={handleApprovalClick}
      >
        {isApprovalVisible && (
          <>
            <ListWrapDiv>
              <table>
                <colgroup>
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "5%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "auto" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "12%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t("L0048")}</th>
                    <th>{t("L0049")}</th>
                    <th>{t("L0050")}</th>
                    <th>{t("L0051")}</th>
                    <th>{t("L0002")}</th>
                    <th>{t("L0661")}</th>
                    <th>{t("L0295")}</th>
                    <th>{t("L0392")}</th>
                    <th>{t("L0003")}</th>
                    <th>{t("L0663")}</th>
                    <th>{t("L0104")}</th>
                    <th>{t("L0440")}</th>
                  </tr>
                </thead>
                <tbody>
                  {approvalData.length === 0 ? (
                    <tr>
                      <td colSpan={11} style={{ textAlign: "center" }}>
                        {t("L0094")}
                        <br />
                        {t("L0095")}
                      </td>
                    </tr>
                  ) : (
                    approvalData.map((data, rowIndex) => (
                      <tr key={rowIndex}>
                        <td>{t(`AREA:${data.area}`)}</td>
                        <td>{t(`UNITED:${data.united}`)}</td>
                        <td>{t(`CNTRY:${data.country}`)}</td>
                        <td>{data.loc}</td>
                        <td>{data.userId}</td>
                        <td>{data.location}</td>
                        <td>{t(`AUTH:${data.auth}`)}</td>
                        <td>{t(`MENU_AUTH:${data.menuAuth}`)}</td>
                        <td>{data.email}</td>
                        <td>{data.useYn}</td>
                        <td>
                          {" "}
                          {data.createDT
                            ? moment(data.createDT).format("YYYY-MM-DD HH:mm:ss")
                            : ""}
                        </td>
                        <td>
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              color: "#002c5f",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleApprovalApprove(data.userInfoId)
                            }
                          >
                            {t("L0293")}
                          </button>
                          <br />
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              color: "#002c5f",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleApprovalReject(data.userInfoId)
                            }
                          >
                            {t("L0294")}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ListWrapDiv>
            <PageBar
              TotalCount={approvalTotalCount}
              CurrentPage={approvalCurPage}
              CountPerPage={pageSize}
              MaxPageBarCount={10}
              onPageСhange={approvalPageChange}
            ></PageBar>
          </>
        )}
      </BoxAreaDiv>

      <BoxAreaDiv
        title={t("L0032") + ` (${unlockTotalCount})`}
        className={unlockClass}
        onClick={handleUnlockClick}
      >
        {isUnlockVisible && (
          <>
            <ListWrapDiv>
              <table>
                <colgroup>
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "5%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "auto" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "12%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t("L0048")}</th>
                    <th>{t("L0049")}</th>
                    <th>{t("L0050")}</th>
                    <th>{t("L0051")}</th>
                    <th>{t("L0002")}</th>
                    <th>{t("L0661")}</th>
                    <th>{t("L0295")}</th>
                    <th>{t("L0392")}</th>
                    <th>{t("L0003")}</th>
                    <th>{t("L0663")}</th>
                    <th>{t("L0104")}</th>
                    <th>{t("L0440")}</th>
                  </tr>
                </thead>
                <tbody>
                  {unlockData.length === 0 ? (
                    <tr>
                      <td colSpan={11} style={{ textAlign: "center" }}>
                        {t("L0094")}
                        <br />
                        {t("L0095")}
                      </td>
                    </tr>
                  ) : (
                    unlockData.map((data, rowIndex) => (
                      <tr key={rowIndex}>
                        <td>{t(`AREA:${data.area}`)}</td>
                        <td>{t(`UNITED:${data.united}`)}</td>
                        <td>{t(`CNTRY:${data.country}`)}</td>
                        <td>{data.loc}</td>
                        <td>{data.userId}</td>
                        <td>{data.location}</td>
                        <td>{t(`AUTH:${data.auth}`)}</td>
                        <td>{t(`MENU_AUTH:${data.menuAuth}`)}</td>
                        <td>{data.email}</td>
                        <td>{data.useYn}</td>
                        <td>
                          {" "}
                          {data.createDT
                            ? moment(data.createDT).format("YYYY-MM-DD HH:mm:ss")
                            : ""}
                        </td>
                        <td>
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              color: "#002c5f",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() => handleUnlockApprove(data.userInfoId)}
                          >
                            {t("L0293")}
                          </button>
                          <br />
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              color: "#002c5f",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() => handleUnlockReject(data.userInfoId)}
                          >
                            {t("L0294")}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ListWrapDiv>
            <PageBar
              TotalCount={unlockTotalCount}
              CurrentPage={unlockCurPage}
              CountPerPage={pageSize}
              MaxPageBarCount={10}
              onPageСhange={unlockPageChange}
            />
          </>
        )}
      </BoxAreaDiv>
    </div>
  );
};
export default UserRequestManagementPage;
